import React, { useEffect, useState } from 'react';
import '../styles/MontierExplainModal.css';
import { formatForensicValue } from '../utils/forensicDisplay';

const METRIC_LABELS = {
    beneishMScore: 'Beneish M-Score',
    montierCScore: 'Montier C-Score',
    sloanAccrualRatio: 'Sloan Accrual Ratio',
    piotroskiFScore: 'Piotroski F-Score',
    altmanZScore: 'Altman Z-Score (Z″)',
};

const verdictClass = (verdict) => {
    const label = (verdict || '').toLowerCase();
    if (
        label.includes('red')
        || label.includes('high concern')
        || label === 'manipulator'
        || label === 'distress'
        || label === 'quality risk'
        || label === 'weak'
    ) {
        return 'fired';
    }
    if (label === 'watch' || label === 'grey' || label === 'caution' || label === 'average') return 'watch';
    if (label === 'clean' || label === 'safe' || label === 'strong' || label === 'quality') return 'clear';
    return 'unavailable';
};

const explanationPayload = (explanation) => {
    if (!explanation) return null;
    const why = explanation.why || explanation.summary || explanation.aggregate_verdict;
    const plain = explanation.plain_language || explanation.plainLanguage;
    const verdict = explanation.verdict;
    if (!why && !plain && !verdict) return null;
    return {
        verdict,
        summary: why,
        why,
        plain_language: plain,
        confidence: explanation.confidence,
        top_reasons: Array.isArray(explanation.top_reasons) ? explanation.top_reasons : [],
        per_metric_explanations: explanation.per_metric_explanations || explanation.perMetricExplanations || null,
    };
};

const formatCell = (value, unit) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const n = Number(value);
    if (unit === 'days') return `${n.toFixed(0)} days`;
    if (unit === '%') return `${n.toFixed(1)}%`;
    if (unit === 'Cr') return `${n.toLocaleString('en-IN')} Cr`;
    if (unit === 'x') return n.toLocaleString('en-IN', { maximumFractionDigits: 3 });
    return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const formatChange = (pct) => {
    if (pct == null || Number.isNaN(Number(pct))) return '—';
    const n = Number(pct);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
};

const formatRange = (from, to, unit, changePct) => {
    const start = formatCell(from, unit);
    const end = formatCell(to, unit);
    const pct = changePct != null && !Number.isNaN(Number(changePct))
        ? ` (${formatChange(changePct)})`
        : '';
    if (from != null && to != null) return `${start} → ${end}${pct}`;
    if (to != null) return `${end}${pct}`;
    return '—';
};

const testStateLabel = (test) => {
    if (!test?.available) return 'unavailable';
    return test.fired ? 'FIRED' : 'not fired';
};

const NumbersTable = ({ card }) => {
    if (!card) return null;
    const tests = card.name === 'montierCScore' && Array.isArray(card.tests) ? card.tests : [];
    if (tests.length > 0) {
        return (
            <section className="mex-section">
                <h3 className="mex-heading">This company’s numbers</h3>
                <table className="mex-table">
                    <thead>
                        <tr>
                            <th>Test</th>
                            <th>Prior → current</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tests.map((test) => (
                            <tr key={test.name}>
                                <td>{test.displayName}</td>
                                <td>{formatRange(test.from, test.to, test.unit, test.changePct)}</td>
                                <td>{testStateLabel(test)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        );
    }

    const components = card.name === 'beneishMScore' && Array.isArray(card.components) ? card.components : [];
    if (components.length > 0) {
        return (
            <section className="mex-section">
                <h3 className="mex-heading">This company’s numbers</h3>
                <table className="mex-table">
                    <thead>
                        <tr>
                            <th>Component</th>
                            <th>Value</th>
                            <th>Contribution</th>
                        </tr>
                    </thead>
                    <tbody>
                        {components.map((row) => (
                            <tr key={row.name || row.displayName}>
                                <td>{row.displayName || row.name}</td>
                                <td>{row.value == null ? '—' : Number(row.value).toFixed(3)}</td>
                                <td>{row.contribution == null ? '—' : Number(row.contribution).toFixed(3)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        );
    }

    const inputs = Array.isArray(card.scoreExplain?.inputs) ? card.scoreExplain.inputs : [];
    if (inputs.length === 0) return null;
    const fromYear = card.scoreExplain?.years?.from ? `FY${card.scoreExplain.years.from}` : 'Prior';
    const toYear = card.scoreExplain?.years?.to ? `FY${card.scoreExplain.years.to}` : 'Current';
    const showChange = inputs.some((row) => row.changePct != null);
    return (
        <section className="mex-section">
            <h3 className="mex-heading">This company’s numbers</h3>
            <table className="mex-table">
                <thead>
                    <tr>
                        <th>Line</th>
                        <th>{fromYear}</th>
                        <th>{toYear}</th>
                        {showChange && <th>Change</th>}
                    </tr>
                </thead>
                <tbody>
                    {inputs.map((row) => (
                        <tr key={row.label}>
                            <td>{row.label}</td>
                            <td>{formatCell(row.from, row.unit)}</td>
                            <td>{formatCell(row.to, row.unit)}</td>
                            {showChange && (
                                <td className="mex-table__change">{formatChange(row.changePct)}</td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
};

const MetricSummaryTable = ({ explanations }) => {
    const rows = Object.entries(explanations || {})
        .filter(([name]) => METRIC_LABELS[name])
        .map(([name, expl]) => ({
            name,
            label: METRIC_LABELS[name],
            verdict: expl?.verdict || '—',
        }));
    if (!rows.length) return null;
    return (
        <section className="mex-section">
            <h3 className="mex-heading">This company’s numbers</h3>
            <table className="mex-table">
                <thead>
                    <tr>
                        <th>Screen</th>
                        <th>Verdict</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.name}>
                            <td>{row.label}</td>
                            <td>{row.verdict}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
};

export default function ForensicAgentModal({
    open,
    title,
    companyId,
    basis = 'consolidated',
    metric,
    apiUrl,
    explanation,
    card = null,
    onClose,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) {
            setPayload(null);
            setError(null);
            setLoading(false);
            return undefined;
        }
        const fromPrefetch = explanationPayload(explanation);
        if (fromPrefetch) {
            setLoading(false);
            setError(null);
            setPayload(fromPrefetch);
            return undefined;
        }
        if (!companyId || !apiUrl) return undefined;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setPayload(null);
        const params = new URLSearchParams({ basis: basis || 'consolidated' });
        if (metric) params.set('metric', metric);
        const url = `${apiUrl}/finlytics/forensic/${encodeURIComponent(companyId)}/verdict?${params.toString()}`;
        fetch(url, { signal: controller.signal })
            .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
            .then(({ ok, body }) => {
                if (!ok || !body?.success) {
                    throw new Error(body?.message || 'Could not load forensic agent.');
                }
                const data = body.data || null;
                const sliced = metric && data?.per_metric_explanations?.[metric]
                    ? explanationPayload({
                        ...data.per_metric_explanations[metric],
                        confidence: data.confidence,
                        top_reasons: data.top_reasons,
                    })
                    : null;
                setPayload(sliced || explanationPayload(data));
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                setError(err.message || 'Could not load forensic agent.');
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [open, companyId, basis, metric, apiUrl, explanation]);

    if (!open) return null;

    const reasons = Array.isArray(payload?.top_reasons) ? payload.top_reasons : [];
    const whyText = payload?.why || payload?.summary;
    const plainText = payload?.plain_language || payload?.plainLanguage;
    const printValue = card
        ? formatForensicValue(card.name, card.value, card.unit)
        : null;

    return (
        <div
            className="mex-overlay mex-overlay--nested"
            onClick={(event) => {
                event.stopPropagation();
                onClose?.();
            }}
            role="presentation"
        >
            <div
                className="mex-panel mex-panel--briefing"
                role="dialog"
                aria-modal="true"
                aria-labelledby="forensic-agent-title"
                onClick={(event) => event.stopPropagation()}
            >
                <header className="mex-header">
                    <div className="mex-title-row">
                        <h2 id="forensic-agent-title" className="mex-title">
                            {title || 'Forensic agent'}
                        </h2>
                        <button type="button" className="mex-close" onClick={onClose} aria-label="Close">
                            ✕
                        </button>
                    </div>
                    {payload?.verdict && (
                        <span className={`mex-verdict mex-verdict--${verdictClass(payload.verdict)}`}>
                            {payload.verdict}
                        </span>
                    )}
                </header>
                <div className="mex-body">
                    {loading && <p>Reading the scored flags…</p>}
                    {error && <p className="mex-section--caveat">{error}</p>}
                    {!loading && !error && payload && (
                        <>
                            {plainText && (
                                <section className="mex-section">
                                    <p>{plainText}</p>
                                </section>
                            )}
                            {card && (
                                <section className="mex-section">
                                    <h3 className="mex-heading">This print</h3>
                                    <p>
                                        {printValue}
                                        {card.year ? ` · FY${card.year}` : ''}
                                        {card.thresholdLabel ? ` · ${card.thresholdLabel}` : ''}
                                    </p>
                                </section>
                            )}
                            {card
                                ? <NumbersTable card={card} />
                                : <MetricSummaryTable explanations={payload.per_metric_explanations} />}
                            {whyText && (
                                <section className="mex-section">
                                    <h3 className="mex-heading">How this print reads</h3>
                                    <p>{whyText}</p>
                                </section>
                            )}
                            {reasons.length > 0 && (
                                <section className="mex-section">
                                    <h3 className="mex-heading">What to watch</h3>
                                    <ol className="mex-steps">
                                        {reasons.map((reason) => (
                                            <li key={reason}>{reason}</li>
                                        ))}
                                    </ol>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
